import { ensureAccessToken } from '../modules/api';
import { state } from '../modules/state';
import { createRemoteFetcher } from '../modules/remoteFetch';
import { applyModeBanner } from '../modules/syncManager';
import { mapRemoteDocument } from '../modules/lotOperations';
import { createRealTimeSubscriber } from '../modules/realTime';
import { els } from '../modules/ui';
import {
  renderUserIdentity as renderUserIdentityHelper,
  hideAuthError as hideAuthErrorHelper,
  updateViewModeToggle as updateViewModeToggleHelper,
} from '../uiHelpers';
import {
  applyPendingProfileIfAny,
  createRenderFunction,
  getLastRenderState,
  setupVisibilityChangeHandling,
} from '../coordination';
import { checkLogoutFlag } from './authentication';
import { detectInitialMode } from './modeDetection';
import { initializeLayoutManager } from './layoutManager';
import { triggerImmediatePoll } from '../modules/polling';
import type { loadConfig } from '../modules/config';

export interface PortalRuntimeContext {
  cfg: PortalConfig;
  state: typeof state;
  render: ReturnType<typeof createRenderFunction>;
  DEBUG: boolean;
  pendingViewModeRefreshRef: { value: boolean };
  hasLoadedInitialLotsRef: { value: boolean };
  startRemoteLotsFetch: () => void;
  disconnectRealTime: (reason?: string) => void;
  applyModeBannerForConfig: () => void;
  renderUserIdentity: () => void;
  hideAuthError: () => void;
  updateViewModeToggle: () => void;
  applyPendingProfile: () => void;
  lastProfileSaveStatusRef: { value: number | null };
  logout: {
    AUTH0_DOMAIN: string;
    AUTH0_CLIENT_ID: string;
    logoutReturnUrl: string;
    useAuth0Logout: boolean;
    wasLoggedOut: boolean;
  };
}

export type PortalConfig = NonNullable<Awaited<ReturnType<typeof loadConfig>>>;

export function createPortalRuntimeContext(cfg: PortalConfig): PortalRuntimeContext {
  initializeLayoutManager();
  detectInitialMode(state);

  const DEBUG = /[?&]debug=1(?!d)/i.test(window.location.search || '');
  const pendingViewModeRefreshRef = { value: false };
  const hasLoadedInitialLotsRef = { value: false };
  const render = createRenderFunction(state);
  const applyModeBannerForConfig = () => applyModeBanner(cfg);

  (window as any).__render = render;

  const realTimeSubscriber = createRealTimeSubscriber({
    cfg,
    state,
    ensureAccessToken,
    mapRemoteDocument,
    render,
    triggerPoll: () => triggerImmediatePoll(cfg, mapRemoteDocument, render, 'submission'),
  });

  const startRemoteLotsFetch = createRemoteFetcher({
    cfg,
    state,
    ensureAccessToken,
    render,
    applyModeBanner: applyModeBannerForConfig,
    pendingViewModeRefreshRef,
    hasLoadedInitialLotsRef,
    DEBUG,
    realTimeSubscriber,
  });

  setupVisibilityChangeHandling({ cfg, mapRemoteDocument, render }, state, startRemoteLotsFetch);

  const renderUserIdentity = () => renderUserIdentityHelper(state, els);
  const hideAuthError = () => hideAuthErrorHelper(els);
  const updateViewModeToggle = () => updateViewModeToggleHelper(state, els, getLastRenderState());
  const applyPendingProfile = () => applyPendingProfileIfAny(cfg);
  const lastProfileSaveStatusRef = { value: 0 as number | null };

  const AUTH0_DOMAIN = cfg?.auth0?.domain || 'lotlingo.us.auth0.com';
  const AUTH0_CLIENT_ID = cfg?.auth0?.clientId || '';
  const wasLoggedOut = checkLogoutFlag();
  const defaultLogoutReturnUrl = `${window.location.origin}/?logged_out=true`;
  const logoutReturnUrl = (() => {
    const configured = cfg?.auth0?.logoutReturnTo;
    if (typeof configured === 'string') {
      const trimmed = configured.trim();
      if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
      }
      if (trimmed.startsWith('/')) {
        return `${window.location.origin}${trimmed}`;
      }
    }
    return defaultLogoutReturnUrl;
  })();
  const isSwaPreviewHost = /\.azurestaticapps\.net$/i.test(window.location.hostname);
  const disableAuth0Logout =
    cfg?.auth0?.disableAuth0Logout === true ||
    (isSwaPreviewHost && cfg?.auth0?.allowPreviewLogout !== true);
  const useAuth0Logout = !disableAuth0Logout;

  return {
    cfg,
    state,
    render,
    DEBUG,
    pendingViewModeRefreshRef,
    hasLoadedInitialLotsRef,
    startRemoteLotsFetch,
    disconnectRealTime: reason => realTimeSubscriber.disconnect(reason, true),
    applyModeBannerForConfig,
    renderUserIdentity,
    hideAuthError,
    updateViewModeToggle,
    applyPendingProfile,
    lastProfileSaveStatusRef,
    logout: {
      AUTH0_DOMAIN,
      AUTH0_CLIENT_ID,
      logoutReturnUrl,
      useAuth0Logout,
      wasLoggedOut,
    },
  };
}
